import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { logError } from "@/lib/errorLogger";

type State = { hasError: boolean; message?: string };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    logError({
      message: error.message || "React error boundary caught error",
      stack: error.stack,
      source: "boundary",
      context: { componentStack: info.componentStack?.slice(0, 2000) },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="text-6xl">🌧️</div>
          <h1 className="font-display text-2xl font-semibold">Something went sideways</h1>
          <p className="text-sm text-muted-foreground">
            We logged this and will look into it. Try reloading the page.
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => window.location.reload()}>Reload</Button>
            <Button variant="outline" onClick={() => (window.location.href = "/")}>Go home</Button>
          </div>
          {this.state.message && (
            <p className="text-xs text-muted-foreground/70 mt-6 break-words">{this.state.message}</p>
          )}
        </div>
      </div>
    );
  }
}